'use client'

import { useEffect, useRef } from 'react'
import ReactHowler from 'react-howler'
import { useAudioStore } from '@/lib/store/audio-store'

/**
 * Global audio player component using react-howler
 * This component should be mounted once at the app root level
 */
export default function AudioPlayer() {
  const playerRef = useRef<ReactHowler | null>(null)
  const rafIdRef = useRef<number | null>(null)

  const currentSrc = useAudioStore((state) => state.currentSrc)
  const isPlaying = useAudioStore((state) => state.isPlaying)
  const currentTime = useAudioStore((state) => state.currentTime)
  const setIsPlaying = useAudioStore((state) => state.setIsPlaying)
  const setIsLoading = useAudioStore((state) => state.setIsLoading)
  const setCurrentTime = useAudioStore((state) => state.setCurrentTime)
  const setDuration = useAudioStore((state) => state.setDuration)

  // Handle seeking
  useEffect(() => {
    const player = playerRef.current
    if (!player || !currentSrc || !player.howler) {
      return
    }

    // Only update if the difference is significant to avoid loops
    const currentSeek = player.seek() || 0
    if (Math.abs(currentSeek - currentTime) > 0.5) {
      player.howler.seek(currentTime)
    }
  }, [currentTime, currentSrc])

  // Setup RAF loop for time updates
  const startTimeUpdateLoop = () => {
    stopTimeUpdateLoop()

    const updateTime = () => {
      const player = playerRef.current
      if (player && player.howler && isPlaying) {
        const seek = player.howler.seek() || 0
        setCurrentTime(seek)
        rafIdRef.current = requestAnimationFrame(updateTime)
      }
    }

    rafIdRef.current = requestAnimationFrame(updateTime)
  }

  const stopTimeUpdateLoop = () => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimeUpdateLoop()
    }
  }, [])

  if (!currentSrc) {
    return null
  }

  return (
    <ReactHowler
      ref={playerRef}
      src={currentSrc}
      playing={isPlaying}
      html5={true}
      loop={false}
      onLoad={() => {
        setIsLoading(false)
        const player = playerRef.current
        if (player && player.howler) {
          setDuration(player.howler.duration())
        }
      }}
      onPlay={() => {
        startTimeUpdateLoop()
      }}
      onPause={() => {
        setIsPlaying(false)
        stopTimeUpdateLoop()
      }}
      onStop={() => {
        setIsPlaying(false)
        setCurrentTime(0)
        stopTimeUpdateLoop()
      }}
      onEnd={() => {
        setIsPlaying(false)
        setCurrentTime(0)
        stopTimeUpdateLoop()
      }}
      onLoadError={(_id, error) => {
        console.error('Audio playback error:', error)
        setIsPlaying(false)
        setIsLoading(false)
        stopTimeUpdateLoop()
      }}
    />
  )
}
